/**
 * useStatementLink — the one place that decides how a statement opens.
 *
 * A statement is a document, not a step in a flow: it is read, printed and
 * handed over. Opening it in the same tab throws away whatever the operator
 * was doing — a half-finished checkout, a filtered arrivals list. So every
 * entry point in the deck opens it in its own tab, and every entry point
 * goes through this hook so that stays true in one edit rather than seven.
 */
import { useCallback } from 'react';

import { useLocationUrl } from './useLocationUrl';
import { openInNewTab } from '@/shared/utils/openInNewTab';

export function useStatementLink() {
  const locUrl = useLocationUrl();

  const bookingStatementPath = useCallback(
    (bookingId: string) => locUrl(`/location/bookings/${bookingId}/statement`),
    [locUrl],
  );
  const guestStatementPath = useCallback(
    (guestId: string) => locUrl(`/location/guests/${guestId}/statement`),
    [locUrl],
  );

  const openBookingStatement = useCallback(
    (bookingId: string) => openInNewTab(bookingStatementPath(bookingId)),
    [bookingStatementPath],
  );
  const openGuestStatement = useCallback(
    (guestId: string) => openInNewTab(guestStatementPath(guestId)),
    [guestStatementPath],
  );

  return { bookingStatementPath, guestStatementPath, openBookingStatement, openGuestStatement };
}
